import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { http, errMsg } from "@/lib/api";

const EMPTY = { name: "", room_type_ids: [], adjust_type: "percent", adjust_value: 0, breakfast_included: false, refundable: true,
  cancellation_policy: "", name_en: "", cancellation_policy_en: "", payment_type: "prepaid", min_nights: 1, active: true };

export const RoomScope = ({ rooms, value, onChange, testid }) => (
  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-2 rounded-md border border-slate-200 p-3" data-testid={testid}>
    <label className="flex items-center gap-2 text-sm"><Checkbox checked={value.length === 0} onCheckedChange={() => onChange([])} /> Semua</label>
    {rooms.map((r) => (
      <label key={r.id} className="flex items-center gap-2 text-sm">
        <Checkbox data-testid={`${testid}-${r.id}`} checked={value.includes(r.id)}
          onCheckedChange={(v) => onChange(v ? [...value, r.id] : value.filter((x) => x !== r.id))} /> {r.name}
      </label>
    ))}
  </div>
);

export default function RatePlanDialog({ open, onOpenChange, plan, rooms, propertyId, onSaved }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF(plan ? { ...EMPTY, ...plan } : EMPTY); }, [open, plan]);
  const set = (k, num) => (e) => setF({ ...f, [k]: num ? Number(e.target.value) : e.target.value });

  const save = async () => {
    try {
      if (plan) await http.put(`/extranet/rate-plans/${plan.id}`, f);
      else await http.post(`/extranet/properties/${propertyId}/rate-plans`, f);
      toast.success("Rate plan disimpan");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="extranet max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="rate-plan-dialog">
        <DialogHeader><DialogTitle>{plan ? "Edit rate plan" : "Tambah rate plan"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label>Nama rate plan</Label><Input data-testid="rp-name" value={f.name} onChange={set("name")} placeholder="mis. Room Only, With Breakfast" className="mt-1.5" /></div>
          <div className="sm:col-span-2"><Label>Nama rate plan (English)</Label><Input data-testid="rp-name-en" value={f.name_en || ""} onChange={set("name_en")} placeholder="Room Only, Breakfast Included" className="mt-1.5" /></div>
          <div className="sm:col-span-2"><Label>Berlaku untuk tipe kamar</Label>
            <RoomScope rooms={rooms} value={f.room_type_ids} onChange={(v) => setF({ ...f, room_type_ids: v })} testid="rp-rooms" /></div>
          <div><Label>Aturan harga dari harga kamar</Label>
            <Select value={f.adjust_type} onValueChange={(v) => setF({ ...f, adjust_type: v })}>
              <SelectTrigger data-testid="rp-adjust-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="percent">Persen (%)</SelectItem><SelectItem value="amount">Nominal (Rp)</SelectItem></SelectContent>
            </Select></div>
          <div><Label>{f.adjust_type === "percent" ? "Penyesuaian % (mis. -10 atau 15)" : "Penyesuaian Rp (mis. 150000)"}</Label>
            <Input data-testid="rp-adjust-value" type="number" value={f.adjust_value} onChange={set("adjust_value", true)} className="mt-1.5" /></div>
          <div><Label>Pembayaran</Label>
            <Select value={f.payment_type} onValueChange={(v) => setF({ ...f, payment_type: v })}>
              <SelectTrigger data-testid="rp-payment-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="prepaid">Prepaid (bayar online)</SelectItem><SelectItem value="pay_at_hotel">Bayar di hotel</SelectItem></SelectContent>
            </Select></div>
          <div><Label>Minimal malam</Label><Input data-testid="rp-min-nights" type="number" min={1} value={f.min_nights} onChange={set("min_nights", true)} className="mt-1.5" /></div>
          <label className="flex items-center gap-3 text-sm"><Switch data-testid="rp-breakfast" checked={f.breakfast_included} onCheckedChange={(v) => setF({ ...f, breakfast_included: v })} /> Termasuk sarapan</label>
          <label className="flex items-center gap-3 text-sm"><Switch data-testid="rp-refundable" checked={f.refundable} onCheckedChange={(v) => setF({ ...f, refundable: v })} /> Bisa dibatalkan gratis</label>
          <div className="sm:col-span-2"><Label>Kebijakan pembatalan</Label><Textarea data-testid="rp-policy" value={f.cancellation_policy} onChange={set("cancellation_policy")} className="mt-1.5" /></div>
          <div className="sm:col-span-2"><Label>Kebijakan pembatalan (English)</Label><Textarea data-testid="rp-policy-en" value={f.cancellation_policy_en || ""} onChange={set("cancellation_policy_en")} className="mt-1.5" /></div>
          <label className="flex items-center gap-3 text-sm"><Switch data-testid="rp-active" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /> Aktif dijual</label>
        </div>
        <DialogFooter><button data-testid="rp-save" onClick={save} className="rounded-md bg-[#006CE4] px-5 py-2 font-semibold text-white hover:bg-[#0052B4]">Simpan</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
